import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  sage: '#8FA89E',
  charcoal: '#2d2d2d',
  cream: '#F5F2EA'
};

const ApplicationForm = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    struggle: '',
    goals: '',
    hasProgramBefore: '',
    readyToCommit: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit application to backend (will email responses to you)
      const response = await fetch(`${BACKEND_URL}/api/applications/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        alert('Something went wrong. Please try again or email me directly at hellofromthemorningmethod@gmail.com');
      }
    } catch (error) {
      // Log error only in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Error submitting application:', error);
      }
      alert('Something went wrong. Please try again or email me directly at hellofromthemorningmethod@gmail.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show success message after submission
  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: COLORS.cream }}>
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="bg-white rounded-xl p-10" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <div className="text-6xl mb-6">✓</div>
            <h1 
              className="text-3xl sm:text-4xl font-bold mb-6"
              style={{ fontFamily: "'Playfair Display', serif", color: COLORS.charcoal }}
            >
              Application Received!
            </h1>
            <p className="text-lg mb-4" style={{ color: '#666', lineHeight: '1.7' }}>
              Thank you for applying. I'll review your application and get back to you within 24–48 hours.
            </p>
            <p className="text-base mb-8" style={{ color: '#888' }}>
              Check your inbox for a confirmation email.
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-8 py-3 text-white text-base font-semibold rounded-lg hover:opacity-90 transition-all"
              style={{ backgroundColor: COLORS.sage }}
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.cream }}>
      {/* BACK BUTTON */}
      <div className="max-w-3xl mx-auto px-6 pt-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 hover:opacity-70 transition-opacity"
          style={{ color: COLORS.charcoal }}
        >
          <ArrowLeft size={20} />
          <span className="text-sm font-medium">Back</span>
        </button>
      </div>

      {/* HEADER */}
      <section className="max-w-3xl mx-auto px-6 py-12 text-center">
        <h1 
          className="text-4xl sm:text-5xl font-bold mb-6 leading-tight"
          style={{ fontFamily: "'Playfair Display', serif", color: COLORS.charcoal }}
        >
          Apply to Work With Me
        </h1>
        
        <p className="text-lg mb-4" style={{ color: COLORS.charcoal }}>
          This is for women ready to stop starting over and finally follow through.
        </p>
        
        <p className="text-base" style={{ color: '#666' }}>
          Answer a few quick questions so I can understand where you're at and if this is the right fit.
        </p>
      </section>

      {/* FORM */}
      <section className="max-w-2xl mx-auto px-6 py-10">
        <div className="bg-white rounded-xl p-8" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: COLORS.charcoal }}>
                Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-gray-400 transition-all"
                style={{ borderColor: '#ddd' }}
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: COLORS.charcoal }}>
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-gray-400 transition-all"
                style={{ borderColor: '#ddd' }}
              />
            </div>

            {/* Struggle */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: COLORS.charcoal }}>
                What are you struggling with right now?
              </label>
              <textarea
                name="struggle"
                value={formData.struggle}
                onChange={handleChange}
                required
                rows="4"
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-gray-400 transition-all resize-none"
                style={{ borderColor: '#ddd' }}
              />
            </div>

            {/* Goals */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: COLORS.charcoal }}>
                What do you want to achieve over the next 3–6 months?
              </label>
              <textarea
                name="goals"
                value={formData.goals}
                onChange={handleChange}
                required
                rows="4"
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-gray-400 transition-all resize-none"
                style={{ borderColor: '#ddd' }}
              />
            </div>

            {/* Program Before */}
            <div>
              <label className="block text-sm font-semibold mb-3" style={{ color: COLORS.charcoal }}>
                Have you followed a program before?
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="hasProgramBefore"
                    value="yes"
                    checked={formData.hasProgramBefore === 'yes'}
                    onChange={handleChange}
                    required
                    className="w-4 h-4"
                  />
                  <span style={{ color: '#666' }}>Yes</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="hasProgramBefore"
                    value="no"
                    checked={formData.hasProgramBefore === 'no'}
                    onChange={handleChange}
                    required
                    className="w-4 h-4"
                  />
                  <span style={{ color: '#666' }}>No</span>
                </label>
              </div>
            </div>

            {/* Ready to Commit */}
            <div>
              <label className="block text-sm font-semibold mb-3" style={{ color: COLORS.charcoal }}>
                Are you ready to commit to change?
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="readyToCommit"
                    value="yes"
                    checked={formData.readyToCommit === 'yes'}
                    onChange={handleChange}
                    required
                    className="w-4 h-4"
                  />
                  <span style={{ color: '#666' }}>Yes</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="readyToCommit"
                    value="no"
                    checked={formData.readyToCommit === 'no'}
                    onChange={handleChange}
                    required
                    className="w-4 h-4"
                  />
                  <span style={{ color: '#666' }}>No</span>
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 text-white text-lg font-semibold rounded-lg hover:opacity-90 transition-all disabled:opacity-50"
              style={{ backgroundColor: COLORS.sage }}
            >
              {isSubmitting ? 'Submitting...' : '👉 Submit Application'}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ApplicationForm;
